import React from 'react';
import { useSettings } from '../../contexts/SettingsContext';

/**
 * StatCard - Metric card for analytics dashboards
 * Supports dark/light mode, gradient backgrounds
 * Uses dynamic theme colors from settings
 */
const StatCard = ({
  label = '',
  value = 0,
  icon = null,
  color = null, // override color
  gradient = false,
  trend = null, // { direction: 'up' | 'down', value: '12%' }
  subtitle = '',
  onClick = null,
  isActive = false,
  isLoading = false,
  className = '',
}) => {
  const { settings } = useSettings();
  const primaryColor = color || settings?.theme?.colors?.primary || '#3B5BDB';

  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      onClick={onClick}
      className={`
        relative overflow-hidden rounded-2xl p-5 transition-all duration-300
        ${onClick ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]' : ''}
        ${isActive ? 'ring-2 shadow-lg' : ''}
        ${gradient
          ? 'text-white'
          : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800'
        }
        ${className}
      `}
      style={{
        ...(gradient ? {
          background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}CC)`,
        } : {}),
        ...(isActive ? {
          ringColor: primaryColor,
          boxShadow: `0 0 0 2px ${primaryColor}40`,
        } : {}),
      }}
    >
      {/* Background decoration for gradient cards */}
      {gradient && (
        <div
          className="absolute -right-4 -top-4 w-24 h-24 rounded-full opacity-20"
          style={{ backgroundColor: 'white' }}
        />
      )}

      <div className="relative flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium mb-1 ${
            gradient ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
          }`}>
            {label}
          </p>

          {isLoading ? (
            <div className={`h-8 w-20 rounded ${gradient ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'} animate-pulse`} />
          ) : (
            <p className={`text-2xl font-bold ${
              gradient ? 'text-white' : 'text-gray-900 dark:text-white'
            }`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
          )}

          {subtitle && (
            <p className={`text-xs mt-1 ${
              gradient ? 'text-white/60' : 'text-gray-400 dark:text-gray-500'
            }`}>
              {subtitle}
            </p>
          )}

          {trend && (
            <div className="flex items-center gap-1 mt-2">
              {trend.direction === 'up' ? (
                <svg className={`w-4 h-4 ${gradient ? 'text-green-200' : 'text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ) : (
                <svg className={`w-4 h-4 ${gradient ? 'text-red-200' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              )}
              <span className={`text-xs font-medium ${
                gradient
                  ? trend.direction === 'up' ? 'text-green-200' : 'text-red-200'
                  : trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {trend.value}
              </span>
            </div>
          )}
        </div>

        {/* Icon */}
        {icon && (
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              gradient ? 'bg-white/20' : ''
            }`}
            style={!gradient ? { backgroundColor: `${primaryColor}15` } : {}}
          >
            <div style={!gradient ? { color: primaryColor } : { color: 'white' }}>
              {icon}
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default StatCard;
